import { useState, useRef, useEffect } from 'react';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { adicionarRespostaComAuditoriaConversa, type ConversaCanal, type RespostaConversa } from '@/mocks/backoffice/canaisHelpers';
import { canalConfig } from '@/mocks/backoffice/conversasCanais';

interface TicketChatProps {
  conversa: ConversaCanal;
  onUpdate: () => void;
}

export default function TicketChat({ conversa, onUpdate }: TicketChatProps) {
  const { session } = useBackofficeAuth();
  const [mensagem, setMensagem] = useState('');
  const [isInterna, setIsInterna] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  const usuarioNome = session.nome || 'Usuário';
  const usuarioRole = session.role || 'operador';
  const canalCfg = canalConfig[conversa.canal];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversa.respostas.length, isTyping]);

  const enviarMensagem = () => {
    if (!mensagem.trim()) return;
    adicionarRespostaComAuditoriaConversa(
      conversa.id,
      {
        autor_nome: usuarioNome,
        autor_role: usuarioRole,
        canal_origem: 'chat',
        mensagem: mensagem.trim(),
        interna: isInterna,
      },
      usuarioNome,
      usuarioRole,
    );
    setMensagem('');
    setIsInterna(false);
    onUpdate();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  const formatarHora = (data: string) => {
    return new Date(data).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const agruparPorData = (respostas: RespostaConversa[]) => {
    const grupos: { data: string; itens: RespostaConversa[] }[] = [];
    let dataAtual = '';
    respostas.forEach((r) => {
      const d = formatarData(r.data);
      if (d !== dataAtual) {
        dataAtual = d;
        grupos.push({ data: d, itens: [] });
      }
      grupos[grupos.length - 1].itens.push(r);
    });
    return grupos;
  };

  const grupos = agruparPorData(conversa.respostas);

  return (
    <div className="bg-white rounded-xl border border-gray-100 flex flex-col h-[600px] lg:h-[700px]">
      {/* Chat header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0">
        <div className="flex items-center space-x-2">
          <div className={`w-8 h-8 rounded-full ${canalCfg.bg} flex items-center justify-center`}>
            <i className={`${canalCfg.icon} ${canalCfg.color} text-sm`}></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-800">Chat do Chamado</h3>
            <p className="text-[10px] text-gray-400">{conversa.respostas.length} mensagens · via {canalCfg.label}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-bold ${
            conversa.status === 'aberto' ? 'bg-red-50 text-red-600' :
            conversa.status === 'em_andamento' ? 'bg-blue-50 text-blue-600' :
            conversa.status === 'resolvido' ? 'bg-emerald-50 text-emerald-600' :
            'bg-gray-50 text-gray-500'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${
              conversa.status === 'aberto' ? 'bg-red-500' :
              conversa.status === 'em_andamento' ? 'bg-blue-500' :
              conversa.status === 'resolvido' ? 'bg-emerald-500' :
              'bg-gray-400'
            } ${conversa.status !== 'fechado' && conversa.status !== 'resolvido' ? 'animate-pulse' : ''}`}></span>
            <span>{conversa.status.replace('_', ' ')}</span>
          </span>
        </div>
      </div>

      {/* Messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {grupos.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-3">
              <i className="ri-chat-smile-3-line text-2xl text-emerald-400"></i>
            </div>
            <p className="text-sm font-medium text-gray-400">Nenhuma mensagem ainda</p>
            <p className="text-xs text-gray-300 mt-1">Seja o primeiro a responder</p>
          </div>
        )}

        {grupos.map((grupo) => (
          <div key={grupo.data}>
            {/* Date divider */}
            <div className="flex items-center justify-center my-4">
              <div className="h-px bg-gray-100 flex-1"></div>
              <span className="px-3 text-[10px] text-gray-400 font-medium">{grupo.data}</span>
              <div className="h-px bg-gray-100 flex-1"></div>
            </div>

            {grupo.itens.map((resp) => {
              const isMe = resp.autor_nome === usuarioNome;
              const isWhatsApp = resp.canal_origem === 'whatsapp';
              const isEmail = resp.canal_origem === 'email';
              return (
                <div
                  key={resp.id}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-3`}
                >
                  <div className={`max-w-[75%] ${isMe ? 'order-2' : 'order-1'}`}>
                    <div className={`flex items-end space-x-2 ${isMe ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      {/* Avatar */}
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                          resp.interna
                            ? 'bg-amber-500'
                            : isMe
                            ? 'bg-emerald-600'
                            : 'bg-stone-600'
                        }`}
                        title={resp.autor_nome}
                      >
                        <span className="text-[10px] font-bold text-white">
                          {resp.interna ? '!' : resp.autor_nome.charAt(0)}
                        </span>
                      </div>

                      {/* Bubble */}
                      <div
                        className={`rounded-2xl px-3.5 py-2.5 ${
                          resp.interna
                            ? 'bg-amber-50 border border-amber-200 rounded-bl-none'
                            : isMe
                            ? 'bg-emerald-600 text-white rounded-br-none'
                            : 'bg-gray-100 text-gray-800 rounded-bl-none'
                        }`}
                      >
                        {/* Header inside bubble */}
                        <div className={`flex items-center space-x-1.5 mb-1 ${isMe ? 'justify-end' : ''}`}>
                          <span className={`text-[10px] font-bold ${
                            resp.interna ? 'text-amber-700' :
                            isMe ? 'text-emerald-100' : 'text-gray-500'
                          }`}>
                            {resp.autor_nome}
                          </span>
                          {resp.interna && (
                            <span className="px-1 py-0.5 bg-amber-200 text-amber-700 text-[8px] font-bold rounded uppercase">
                              Interna
                            </span>
                          )}
                          {(isWhatsApp || isEmail) && (
                            <span className={`flex items-center space-x-0.5 px-1 py-0.5 rounded text-[8px] font-bold ${
                              isWhatsApp ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              <div className="w-3 h-3 flex items-center justify-center">
                                <i className={`${isWhatsApp ? 'ri-whatsapp-line' : 'ri-mail-line'} text-[8px]`}></i>
                              </div>
                              <span>{isWhatsApp ? 'WA' : 'Email'}</span>
                            </span>
                          )}
                        </div>

                        {/* Message text */}
                        <p className={`text-xs leading-relaxed ${
                          isMe ? 'text-white' : 'text-gray-700'
                        }`}>
                          {resp.mensagem}
                        </p>

                        {/* Time */}
                        <p className={`text-[9px] mt-1 ${
                          isMe ? 'text-emerald-200' :
                          resp.interna ? 'text-amber-400' : 'text-gray-400'
                        }`}>
                          {formatarHora(resp.data)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex items-center space-x-2 px-2">
            <div className="w-6 h-6 rounded-full bg-stone-500 flex items-center justify-center">
              <span className="text-[9px] font-bold text-white">?</span>
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-bl-none px-3 py-2 flex items-center space-x-1">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
            </div>
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 shrink-0">
        <div className="space-y-2">
          <div className="relative">
            <textarea
              ref={inputRef}
              value={mensagem}
              onChange={(e) => {
                setMensagem(e.target.value);
                setIsTyping(e.target.value.length > 0);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua mensagem... (Shift+Enter para nova linha)"
              rows={2}
              className="w-full px-3.5 py-2.5 pr-12 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all resize-none"
            />
            <button
              onClick={enviarMensagem}
              disabled={!mensagem.trim()}
              className="absolute right-2 bottom-2 w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center space-x-2 cursor-pointer group">
              <input
                type="checkbox"
                checked={isInterna}
                onChange={(e) => setIsInterna(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors">
                Nota interna (não visível para o cliente)
              </span>
            </label>
            <p className="text-[10px] text-gray-400">
              Enter para enviar · Shift+Enter para nova linha
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}